import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Skeleton } from '@/components/ui/skeleton'
import { useHubSpotSegments, useSyncContacts } from '@/hooks/useApi'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

export type HubSpotSyncMode = 'all' | 'segment'

/** Query parameter that opens the Contacts sync dialog on a HubSpot list. */
export const SYNC_FROM_SEGMENT_PARAM = { name: 'sync', value: 'segment' } as const

/** Link to the Contacts page with the sync dialog open on a HubSpot list. */
export const SYNC_FROM_SEGMENT_PATH = `/contacts?${SYNC_FROM_SEGMENT_PARAM.name}=${SYNC_FROM_SEGMENT_PARAM.value}`

interface HubSpotSyncDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: HubSpotSyncMode
  onModeChange: (mode: HubSpotSyncMode) => void
}

/**
 * Sync contacts from HubSpot: the whole portal, or a single list picked by name
 * among the portal's lists, most recently created first (issue #318).
 */
export function HubSpotSyncDialog({ open, onOpenChange, mode, onModeChange }: HubSpotSyncDialogProps) {
  const { t } = useTranslation(['contacts', 'common'])
  const { toast } = useToast()
  const [selectedSegment, setSelectedSegment] = useState('')
  const syncContacts = useSyncContacts()
  const hubspotSegments = useHubSpotSegments({ enabled: open && mode === 'segment' })

  const segmentOptions = (hubspotSegments.data ?? []).map((segment) => ({
    value: segment.segment_id,
    label: segment.name,
    // An unknown size (null) shows no hint rather than "0 contatti" (issue #324).
    hint: segment.size === null ? undefined : t('syncDialog.segmentSize', { count: segment.size }),
  }))
  // A reload can drop the picked list (deleted on HubSpot): never sync a stale id.
  const segmentToSync = segmentOptions.some((option) => option.value === selectedSegment)
    ? selectedSegment
    : ''

  const handleSync = () => {
    syncContacts.mutate(
      {
        mode,
        segment_id: mode === 'segment' ? segmentToSync : undefined,
      },
      {
        onSuccess: () => {
          onOpenChange(false)
          toast({
            title: t('syncToast.inProgressTitle'),
            description: t('syncToast.inProgressDescription'),
          })
        },
        onError: () => {
          toast({
            variant: 'destructive',
            title: t('syncToast.errorTitle'),
            description: t('syncToast.errorDescription'),
          })
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('syncDialog.title')}</DialogTitle>
          <DialogDescription>{t('syncDialog.description')}</DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('syncDialog.mode')}</label>
            <Select value={mode} onValueChange={(value) => onModeChange(value as HubSpotSyncMode)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('syncDialog.allContacts')}</SelectItem>
                <SelectItem value="segment">{t('syncDialog.fromSegment')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === 'segment' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <label className="text-sm font-medium">{t('syncDialog.selectSegment')}</label>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => hubspotSegments.refetch()}
                  disabled={hubspotSegments.isFetching}
                  aria-label={t('syncDialog.refreshSegments')}
                  title={t('syncDialog.refreshSegments')}
                >
                  <RefreshCw
                    className={cn('h-4 w-4', hubspotSegments.isFetching && 'animate-spin')}
                    aria-hidden="true"
                  />
                </Button>
              </div>
              {hubspotSegments.isError && (
                <p role="alert" className="text-sm text-destructive">
                  {t('syncDialog.segmentsLoadError')}
                </p>
              )}
              {hubspotSegments.isLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : hubspotSegments.data && (
                <SearchableSelect
                  value={segmentToSync}
                  onValueChange={setSelectedSegment}
                  options={segmentOptions}
                  placeholder={t('syncDialog.selectSegmentPlaceholder')}
                  searchPlaceholder={t('syncDialog.searchSegment')}
                  emptyText={t('syncDialog.noSegmentFound')}
                  aria-label={t('syncDialog.selectSegment')}
                />
              )}
              <p className="text-xs text-muted-foreground">{t('syncDialog.segmentsOrder')}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleSync}
            disabled={syncContacts.isPending || (mode === 'segment' && !segmentToSync)}
          >
            {syncContacts.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('syncDialog.syncing')}
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('syncDialog.sync')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
